import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { RealtimeMessage, SrLiveMessage } from '@cloudsign/shared';

/**
 * The building channel (contract s6). A building socket carries no site, so it cannot share the portal's per-site hub:
 * it subscribes with `{ channel: 'building' }` and the server joins the building's own room. Messages are `SrLiveMessage`
 * and are idempotent upserts; after a dropped connection the page refetches its snapshot (`onReconnect`).
 */

const useMock = import.meta.env.VITE_MOCK === '1' || import.meta.env.MODE === 'test';

interface Handlers {
  connected(reconnect: boolean): void;
  disconnected(): void;
  message(raw: unknown): void;
}
interface Transport {
  close(): void;
}

function socketTransport(h: Handlers): Transport {
  const socket = io({ path: '/api/socket.io', withCredentials: true });
  let first = true;
  socket.on('connect', () => {
    socket.emit('subscribe', { channel: 'building' }, () => undefined);
    h.connected(!first);
    first = false;
  });
  socket.on('disconnect', () => h.disconnected());
  socket.on('control', () => h.disconnected());
  socket.on('message', (raw: unknown) => h.message(raw));
  return { close: () => void socket.disconnect() };
}

const mockListeners = new Set<Handlers>();
function mockTransport(h: Handlers): Transport {
  mockListeners.add(h);
  queueMicrotask(() => {
    if (mockListeners.has(h)) h.connected(false);
  });
  return { close: () => void mockListeners.delete(h) };
}

/** MSW handlers and tests push fake building messages through this. */
export const buildingMockLive = {
  emit(data: unknown): void {
    for (const h of [...mockListeners]) h.message({ channel: 'building', siteId: null, data, at: new Date().toISOString() });
  },
  disconnect(): void {
    for (const h of [...mockListeners]) h.disconnected();
  },
  reconnect(): void {
    for (const h of [...mockListeners]) h.connected(true);
  },
};

export function useBuildingLive(onMessage: (m: SrLiveMessage) => void, onReconnect: () => void): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  const msgRef = useRef(onMessage);
  const reRef = useRef(onReconnect);
  useEffect(() => {
    msgRef.current = onMessage;
    reRef.current = onReconnect;
  });
  useEffect(() => {
    const transport = (useMock ? mockTransport : socketTransport)({
      connected: (again) => {
        setConnected(true);
        if (again) reRef.current();
      },
      disconnected: () => setConnected(false),
      message: (raw) => {
        const env = RealtimeMessage.safeParse(raw);
        if (!env.success || env.data.channel !== 'building') return;
        const m = SrLiveMessage.safeParse(env.data.data);
        if (m.success) msgRef.current(m.data);
      },
    });
    return () => {
      transport.close();
      setConnected(false);
    };
  }, []);
  return { connected };
}
